import React, { useState, useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
// Fix: Use namespace import to bypass missing exported member errors in react-router-dom
import * as ReactRouterDom from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Person } from '../types';
import { Search, Plus, MapPin, SortAsc, Download, Upload } from 'lucide-react';
import { PersonFormModal } from '../features/people/PersonFormModal';
import { PersonDeleteDialog } from '../features/people/PersonDeleteDialog';
import { PersonTable } from '../features/people/PersonTable';
import { Button } from '../components/ui/Button';
import { SearchableSelect } from '../components/ui/SearchableSelect';
import { useAuth } from '../context/AuthContext';
import Papa from 'papaparse';

const { useNavigate } = ReactRouterDom as any;

type SortVector = 'first_name_asc' | 'first_name_desc' | 'last_name_asc' | 'last_name_desc';

const PeopleList: React.FC = () => {
  const navigate = useNavigate();
  const { profile } = useAuth();
  
  // Persistence Helpers
  const getSaved = (key: string, def: any) => {
    const val = localStorage.getItem(`people_list_${key}`);
    if (val === null) return def;
    try { return JSON.parse(val); } catch { return val; }
  };

  const [searchTerm, setSearchTerm] = useState(() => getSaved('searchTerm', ''));
  const [cityFilter, setCityFilter] = useState(() => getSaved('city', ''));
  const [selectedOrgId, setSelectedOrgId] = useState<string>(() => getSaved('org', ''));
  const [selectedAffiliationId, setSelectedAffiliationId] = useState<string>(() => getSaved('affiliation', ''));
  const [sortField, setSortField] = useState<string>(() => getSaved('sortField', 'first_name'));
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(() => getSaved('sortDirection', 'asc'));
  const [page, setPage] = useState(() => getSaved('page', 1));
  const [pageSize, setPageSize] = useState(() => getSaved('pageSize', 30));

  // Sync to LocalStorage
  useEffect(() => { localStorage.setItem('people_list_searchTerm', JSON.stringify(searchTerm)); }, [searchTerm]);
  useEffect(() => { localStorage.setItem('people_list_city', JSON.stringify(cityFilter)); }, [cityFilter]);
  useEffect(() => { localStorage.setItem('people_list_org', JSON.stringify(selectedOrgId)); }, [selectedOrgId]);
  useEffect(() => { localStorage.setItem('people_list_affiliation', JSON.stringify(selectedAffiliationId)); }, [selectedAffiliationId]);
  useEffect(() => { localStorage.setItem('people_list_sortField', JSON.stringify(sortField)); }, [sortField]);
  useEffect(() => { localStorage.setItem('people_list_sortDirection', JSON.stringify(sortDirection)); }, [sortDirection]);
  useEffect(() => { localStorage.setItem('people_list_page', JSON.stringify(page)); }, [page]);
  useEffect(() => { localStorage.setItem('people_list_pageSize', JSON.stringify(pageSize)); }, [pageSize]);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingPerson, setEditingPerson] = useState<Person | null>(null);
  const [deletingPerson, setDeletingPerson] = useState<Person | null>(null);
  
  const isAdmin = profile?.role === 'system_admin';

  const { data: peopleData, isLoading } = useQuery({
    queryKey: ['people', searchTerm, cityFilter, selectedOrgId, selectedAffiliationId, sortField, sortDirection, page, pageSize],
    queryFn: async () => {
      let query = supabase.from('people').select('*, organizations(id, name), affiliations(id, label)', { count: 'exact' });
      
      if (searchTerm) {
        query = query.or(`first_name.ilike.%${searchTerm}%,last_name.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%`);
      }
      if (cityFilter) {
        query = query.ilike('city', `%${cityFilter}%`);
      }
      if (selectedOrgId) {
        query = query.eq('organization_id', selectedOrgId);
      }
      if (selectedAffiliationId) {
        query = query.eq('affiliation_id', selectedAffiliationId);
      }

      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;

      const { data, error, count } = await query
        .order(sortField, { ascending: sortDirection === 'asc' })
        .range(from, to);

      if (error) throw error;
      return { people: data as Person[], total: count || 0 };
    }
  });

  const people = peopleData?.people || [];
  const totalCount = peopleData?.total || 0;

  const { data: orgs } = useQuery({
    queryKey: ['all-organizations-filter'],
    queryFn: async () => {
      const { data } = await supabase.from('organizations').select('id, name').order('name');
      return data || [];
    }
  });

  const { data: affiliations } = useQuery({
    queryKey: ['lookup', 'affiliations'],
    queryFn: async () => {
      const { data } = await supabase.from('affiliations').select('id, label').order('label');
      return (data || []).map(a => ({ id: a.id, name: a.label }));
    }
  });

  const handleExport = () => {
    const data = people.map(p => ({
      'First Name': p.first_name,
      'Last Name': p.last_name,
      'Email': p.email || '—',
      'Phone': p.phone || '—',
      'Organization': p.organizations?.name || '—',
      'Affiliation': p.affiliations?.label || '—',
      'City': p.city || '—',
      'State': p.state || '—'
    }));
    const csv = Papa.unparse(data);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `hfli_people_directory_${new Date().toISOString().slice(0, 10)}.csv`);
    link.click();
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <div className="space-y-6 animate-in fade-in duration-500 pb-20 max-w-7xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6">
        <div>
          <h1 className="text-3xl font-black text-slate-900 uppercase tracking-tight">
            People Directory <span className="text-slate-300 ml-1">
              {searchTerm || cityFilter || selectedOrgId ? `(Showing ${people.length} of ${totalCount})` : `(${totalCount})`}
            </span>
          </h1>
          <p className="text-slate-500 font-bold text-[10px] uppercase tracking-widest mt-1">Registry of Individual Contacts & Roles</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={handleExport} icon={<Download size={18} />}>Export CSV</Button>
          {isAdmin && (
            <Button variant="outline" onClick={() => navigate('/import?type=people')} icon={<Upload size={18} />}>Import CSV</Button>
          )}
          <Button 
            onClick={() => setIsAddModalOpen(true)}
            icon={<Plus size={18} />}
            className="px-8 py-4 shadow-lg shadow-[#FFDB00]/20"
          >
            New Person
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-md shadow-sm border border-slate-200 overflow-hidden">
        <div className="p-6 border-b border-slate-100 grid grid-cols-1 md:grid-cols-5 gap-6 bg-slate-50/30">
          <div className="relative">
            <label className="block text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1 mb-1.5">Search Identity</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
              <input
                type="text"
                placeholder="Name, email..."
                className="w-full pl-10 pr-4 py-2 bg-white border border-slate-200 rounded font-bold text-slate-900 text-[11px] outline-none focus:ring-4 focus:ring-slate-100 transition-all shadow-sm"
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
              />
            </div>
          </div>

          <div>
            <SearchableSelect 
              label="Affiliation"
              options={affiliations || []}
              value={selectedAffiliationId}
              onChange={(val) => { setSelectedAffiliationId(val); setPage(1); }}
              placeholder="All Affiliations"
            />
          </div>

          <div>
            <SearchableSelect 
              label="Institutional Filter"
              options={orgs || []}
              value={selectedOrgId}
              onChange={(val) => { setSelectedOrgId(val); setPage(1); }}
              placeholder="All Organizations"
            />
          </div>

          <div className="relative">
            <label className="block text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1 mb-1.5">Geography</label>
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
              <input
                type="text"
                placeholder="Filter by city..."
                className="w-full pl-10 pr-4 py-2 bg-white border border-slate-200 rounded font-bold text-slate-900 text-[11px] outline-none focus:ring-4 focus:ring-slate-100 transition-all shadow-sm"
                value={cityFilter}
                onChange={(e) => { setCityFilter(e.target.value); setPage(1); }}
              />
            </div>
          </div>

          <div className="relative">
            <label className="block text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1 mb-1.5">Ordering</label>
            <div className="relative">
              <SortAsc size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <select 
                value={`${sortField}_${sortDirection}`} 
                onChange={(e) => {
                  const [field, dir] = e.target.value.split('_');
                  setSortField(field);
                  setSortDirection(dir as 'asc' | 'desc');
                  setPage(1);
                }}
                className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded text-[10px] font-black uppercase tracking-tight outline-none cursor-pointer shadow-sm appearance-none"
              >
                <option value="first_name_asc">First Name A-Z</option>
                <option value="first_name_desc">First Name Z-A</option>
                <option value="last_name_asc">Last Name A-Z</option>
                <option value="last_name_desc">Last Name Z-A</option>
              </select>
            </div>
          </div>
        </div>
        
        {(cityFilter || selectedOrgId || selectedAffiliationId || searchTerm) && (
          <div className="px-6 py-2 border-b border-slate-100 bg-white flex items-center justify-end">
             <button onClick={() => { setCityFilter(''); setSelectedOrgId(''); setSelectedAffiliationId(''); setSearchTerm(''); setPage(1); }} className="text-[9px] font-black text-rose-500 uppercase tracking-widest hover:underline">
               Reset All Viewport Filters
             </button>
          </div>
        )}

        <PersonTable 
          people={people} 
          isLoading={isLoading} 
          onEdit={setEditingPerson}
          onDelete={setDeletingPerson}
        />

        <div className="p-6 bg-slate-50/50 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Records per page</span>
              <select 
                value={pageSize} 
                onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                className="bg-white border border-slate-200 rounded px-2 py-1 text-[10px] font-bold outline-none focus:ring-2 focus:ring-blue-100"
              >
                {[30, 50, 100, 500, 1000].map(size => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
            </div>
            <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">
              Showing {Math.min(totalCount, (page - 1) * pageSize + 1)} - {Math.min(totalCount, page * pageSize)} of {totalCount}
            </span>
          </div>

          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              disabled={page === 1} 
              onClick={() => setPage((p: number) => p - 1)}
              className="text-[9px] font-black uppercase tracking-widest"
            >
              Prev
            </Button>
            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum = page;
                if (totalPages <= 5) pageNum = i + 1;
                else if (page <= 3) pageNum = i + 1;
                else if (page >= totalPages - 2) pageNum = totalPages - 4 + i;
                else pageNum = page - 2 + i;

                return (
                  <button
                    key={pageNum}
                    onClick={() => setPage(pageNum)}
                    className={`w-8 h-8 rounded text-[10px] font-black transition-all ${page === pageNum ? 'bg-slate-900 text-[#FFDB00]' : 'bg-white border border-slate-200 text-slate-400 hover:border-slate-900 hover:text-slate-900'}`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              disabled={page === totalPages || totalPages === 0} 
              onClick={() => setPage((p: number) => p + 1)}
              className="text-[9px] font-black uppercase tracking-widest"
            >
              Next
            </Button>
          </div>
        </div>
      </div>

      {(isAddModalOpen || editingPerson) && (
        <PersonFormModal 
          person={editingPerson} 
          onClose={() => { setIsAddModalOpen(false); setEditingPerson(null); }} 
        />
      )}

      {deletingPerson && (
        <PersonDeleteDialog 
          person={deletingPerson} 
          onClose={() => setDeletingPerson(null)} 
        />
      )}
    </div>
  );
};

export default PeopleList;